import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'
import moment from 'moment-timezone'
import message from 'antd/lib/message'

import { useQuery } from 'hooks'
import { apiManager } from 'helpers'

import { Button, CloseIcon } from 'components/index'
import Modal from 'components/AuthModal/components/Modal'
import Overlay from 'components/AuthModal/components/Overlay'

import ReferralModal from './ReferralModal'
import PaymentModal from './PaymentModal'

import { OVERLAY } from 'components/AuthModal/styles'

const benefits = [
  {
    title: 'EXCLUSIVE',
    text: 'Private Vetted Membership',
  },
  {
    title: 'MATCHMAKING',
    text: 'Match & meet your most compatibles',
  },
  {
    title: 'EXPERIENCES',
    text: 'Members-Only Experiences Designed for Connection',
  },
  {
    title: 'SESSIONS',
    text: 'Program your Al with your values, passions, dreams, and lifestyle.',
  },
  {
    title: 'GIFTING',
    text: 'Receive luxury gift packages bi-monthly from TELEPORT.',
  },
  {
    title: 'PREFERRED REFERRALS',
    text: 'Be a gatekeeper of TELEPORT memberships - get your friends expedited reviews.',
  },
]

const benefitsPreview = benefits.slice(0, 3)

const textStyle = {
  background: 'linear-gradient(180deg, #B6C0E5 0%, #848DAF 50%, #929DC2 100%)',
  'background-clip': 'text',
  '-webkit-background-clip': 'text',
  '-webkit-text-fill-color': 'transparent',
}

const MembershipModal = ({ show, onClose, onComplete }) => {
  const { data, refetch: fetchPlans } = useQuery(
    'memberships/plans',
    apiManager.getMembershipPlans,
    {
      enabled: show,
    }
  )
  const { data: referrer, refetch: fetchReferrer } = useQuery(
    'user/referrer',
    apiManager.getReferrer,
    {
      enabled: show,
    }
  )

  const [selectedPlan, setSelectedPlan] = useState()
  const [clientSecret, setClientSecret] = useState('')
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [isSubscribing, setIsSubscribing] = useState(false)

  const [showBenefits] = useState(true)
  const [showReferralModal, setShowReferralModal] = useState(false)

  const visibleBenefits = showBenefits ? benefits : benefitsPreview

  const plans = useMemo(() => {
    const parsePlan = plan => ({
      ...plan,
      intervalPrice: plan.intervalAmount / 100,
      price: plan.unitAmount / 100,
      date: moment(plan.startsAt).format('LL').toUpperCase(),
      basePlan: plan.basePlan ? parsePlan(plan.basePlan) : undefined,
    })

    return data?.map(parsePlan) || []
  }, [data])

  useEffect(() => {
    if (plans.length) {
      setSelectedPlan(plans.filter(({ defaultSelection }) => defaultSelection)?.[0])
    }
  }, [plans])

  const handleSubscriptionRequest = useCallback(async () => {
    setIsSubscribing(true)

    try {
      const response = await apiManager.subscribeToMembership({ planId: selectedPlan.id })
      setClientSecret(response.data.clientSecret)
      setShowPaymentModal(true)
    } catch (error) {
      message.error(error.message || 'Please try again')
    } finally {
      setIsSubscribing(false)
    }
  }, [selectedPlan])

  const handleSubscription = useCallback(() => {
    onComplete()
    setShowPaymentModal(false)
  }, [onComplete])

  const handleReferralApplication = useCallback(async () => {
    setShowReferralModal(false)
    await fetchPlans()
    await fetchReferrer()
  }, [fetchPlans, fetchReferrer])

  return (
    <>
      <Overlay
        show={show}
        className={classnames(OVERLAY, 'bg-opacity-90')}
        preventEscBackdropClose={true}
      >
        <Modal innerClassName="overflow-y-scroll flex flex-col rounded-t-3xl bg-black overflow-hidden md:border-opacity-60 md:border-solid sm:border-cinder md:rounded-xl">
          <div className="relative flex flex-col text-white justify-between h-full w-full py-4 bg-black px-4 md:px-7 md:py-6 md:w-108 md:h-140 md:max-h-screen">
            <div className="flex flex-col overflow-y-hidden justify-start">
              <div className="">
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-xl font-medium font-ownersWide uppercase md:text-2xl">
                      <div className="mr-3.5">Live in New York?</div>
                    </div>
                    {onClose && (
                      <button
                        className="bg-white bg-opacity-20 w-6 h-6 rounded-full flex items-center justify-center"
                        disabled={isSubscribing}
                        onClick={() => onClose()}
                      >
                        <CloseIcon className="w-2 h-2 text-white" />
                      </button>
                    )}
                  </div>
                  <div className="text-sm opacity-50 mb-4">
                    Apply for TELEPORT’S Premium Membership
                  </div>
                </div>
                {plans.length > 1 && (
                  <div className="flex items-center justify-between mb-2 rounded-full overflow-hidden">
                    {plans?.map(plan => {
                      const selected = plan.id === selectedPlan?.id
                      return (
                        <div
                          key={plan.id}
                          className={classnames(
                            'relative flex flex-col items-center bg-mineShaft flex-shrink-0 py-2 uppercase cursor-pointer',
                            {
                              'opacity-50': !selected,
                            }
                          )}
                          style={{ width: `calc(${(100 / plans.length).toFixed(2)}% - 2px)` }}
                          onClick={() => setSelectedPlan(plan)}
                        >
                          <div className="text-sm font-medium" style={textStyle}>
                            {plan.savings ? `Save ${plan.savings}` : 'BASIC'}
                          </div>
                          <div className="text-base">
                            {plan.intervalCount} {plan.interval}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              <div
                className={classnames(
                  'flex flex-col justify-start rounded-xl mb-8 border-solid border-periwinkle p-4 overflow-y-scroll',
                  {
                    '1flex-grow': showBenefits,
                  }
                )}
              >
                {selectedPlan && (
                  <div className="flex flex-col h-full">
                    <div className="flex-shrink-0">
                      {referrer ? (
                        <div
                          className="mb-2 text-xs text-white uppercase font-medium"
                          style={textStyle}
                        >
                          Referred by {referrer.name}
                        </div>
                      ) : (
                        <button
                          className="p-2 mb-2 text-xs bg-transparent border border-solid border-periwinkle rounded-full text-white uppercase"
                          onClick={() => setShowReferralModal(true)}
                        >
                          <span style={textStyle}>+ REFERRAL</span>
                        </button>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex">
                          <div
                            className="text-6xl tracking-tighter mr-1.5"
                            style={{
                              fontSize: '48px',
                              lineHeight: '48px',
                            }}
                          >
                            {selectedPlan.intervalCount}
                          </div>
                          <div
                            className={classnames('flex flex-col', {
                              'justify-between': selectedPlan.gifted,
                              'justify-center': !selectedPlan.gifted,
                            })}
                          >
                            <div className="text-lg font-medium capitalize">
                              {selectedPlan.interval}
                            </div>
                            <div className="text-sm">
                              <span>{selectedPlan.gifted ? 'FREE' : ''}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-right font-medium">
                          <div className="text-2xl">
                            $
                            {selectedPlan.gifted
                              ? '0'
                              : selectedPlan.intervalPrice.toLocaleString()}
                            <span className="text-sm">/mo</span>
                          </div>
                          {selectedPlan.basePlan && (
                            <div className="text-base opacity-50 line-through">
                              ${selectedPlan.basePlan.intervalPrice.toLocaleString()}/mo
                            </div>
                          )}
                        </div>
                      </div>
                      {selectedPlan.discount && (
                        <div className="text-sm font-medium opacity-80 bg-white bg-opacity-10 rounded-lg px-3 py-4 mt-2">
                          Your first {selectedPlan.discount.label} of membership are free. Your next
                          billing cycle will start {selectedPlan.discount.label} after you are
                          accepted.
                        </div>
                      )}
                      <div className="h-0.5 bg-white opacity-20 w-full my-8" />
                    </div>
                    <div className="min-h-0 overflow-y-scroll">
                      {visibleBenefits.map((benefit, index) => (
                        <div key={index} className="text-sm font-medium mb-3">
                          <div className="capitalize font-ownersWide">{benefit.title}</div>
                          <div className="opacity-50">{benefit.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="">
              <Button
                className="bg-white rounded-full w-full flex-wrap mb-2.5"
                disabled={isSubscribing}
                loading={isSubscribing}
                style={{
                  background:
                    'linear-gradient(177.65deg, #FFFFFF 1.96%, #C0CEFF 98.03%),linear-gradient(0deg, #FFFFFF, #FFFFFF),linear-gradient(0deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15))',
                }}
                onClick={handleSubscriptionRequest}
              >
                <span className="text-black w-full">
                  {selectedPlan?.gifted ? 'CLAIM MEMBERSHIP' : 'RESERVE FOR FREE'}
                </span>
              </Button>
              <div className="opacity-40 text-center">
                {selectedPlan?.discount ? (
                  <div className="text-xs">
                    Your credit card is used for your first billing cycle starts{' '}
                    {selectedPlan.discount.label} after you are accepted. We will you remind you 7
                    days before you are charged.
                  </div>
                ) : (
                  <div className="text-xs">Not charged until accepted</div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      </Overlay>
      <PaymentModal
        show={showPaymentModal}
        clientSecret={clientSecret}
        plan={selectedPlan}
        onClose={() => setShowPaymentModal(false)}
        onComplete={handleSubscription}
      />
      <ReferralModal
        show={showReferralModal}
        onClose={() => setShowReferralModal(false)}
        onComplete={handleReferralApplication}
      />
    </>
  )
}

export default MembershipModal
